import type { Operator, ParsedToken } from '../../../types/QueryBuilder';

import { tokensForExecutableQuery } from './dropIncompleteLogic';
import { isPresentAbsence, isPresentSentinelValue } from './presentSentinel';
import { isTimestampFieldName, normalizeTimestampValue } from './timestampValue';

// =============================================================================
// Lezer (QueryBuilder) → ANTLR (Observability Search) query converter
//
// Mirrors `buildObsSearchPayload` in QueryBuilder.stories.tsx so the production
// metric form and the stories playground emit identical ANTLR for the same
// parsed query. Anything that changes here should change there too.
// =============================================================================

const OBS_NEEDS_QUOTE_RE = /[\s"'`:=><*]/;

function quoteAntlrValue(value: string): string {
  // Force-quote an empty value: bare `field=` is malformed ANTLR; `field=""` round-trips.
  if (value !== '' && !OBS_NEEDS_QUOTE_RE.test(value)) {
    return value;
  }
  return `"${value.replace(/"/g, '\\"')}"`;
}

// Prefer the operator's inverse when ANTLR has one. Operators absent from this
// map retain their spelling and are wrapped in `NOT` instead; silently dropping
// the prefix would reverse the user's query (notably `NOT … starts with`).
const OBS_NEGATED_OP: Partial<Record<Operator, Operator>> = {
  '=': '!=',
  '!=': '=',
  '**': '!**',
  '!**': '**',
  '//': '!//',
  '!//': '//',
};

// Lezer EXPRESSION.expressionType (camelCase) → ANTLR relational prefix (snake_case).
// Unknown types emit a plain `(...)` group.
const OBS_RELATIONAL_PREFIX: Record<string, string> = {
  anySpan: 'any_span',
  anySpan2: 'any_span2',
  anySpan3: 'any_span3',
  noSpan: 'no_span',
  childSpan: 'child_span',
  parentSpan: 'parent_span',
  rootSpan: 'root_span',
  anySession: 'any_session',
};

function filterTokenToAntlr(token: Extract<ParsedToken, { type: 'FILTER' }>): string {
  const { field, operator, value, negated } = token;
  // A timestamp reaches ClickHouse as a bare string literal, so the value a person can read has to
  // become the one ClickHouse converts before it is quoted into the query.
  const toScalar = isTimestampFieldName(field) ? (v: unknown) => normalizeTimestampValue(String(v)) : String;
  const scalars = (Array.isArray(value) ? value : [value]).map(toScalar);

  // PRESENT_VALUE_SENTINEL → EXISTS / NOT EXISTS. XOR the `!` prefix with `!=`
  // so e.g. `field:!=:__present__` produces `field NOT EXISTS`.
  if (isPresentSentinelValue(value)) {
    return `${field} ${isPresentAbsence({ operator, negated }) ? 'NOT EXISTS' : 'EXISTS'}`;
  }

  const inverse = negated ? OBS_NEGATED_OP[operator] : undefined;
  const effectiveOp: Operator = inverse ?? operator;
  const needsNotWrapper = !!negated && inverse === undefined;

  // Multi-value filters expand to a boolean group: positive matchers join with OR,
  // negated with AND. A single value emits a bare term. Every multi-value operator
  // must use this helper or the extra values are silently dropped.
  const group = (join: 'OR' | 'AND', term: (v: string) => string): string =>
    scalars.length > 1 ? `(${scalars.map(term).join(` ${join} `)})` : term(scalars[0]);

  const membershipList = (op: '=' | '!='): string =>
    scalars.length > 1
      ? `${field}${op}(${scalars.map(quoteAntlrValue).join(' OR ')})`
      : `${field}${op}${quoteAntlrValue(scalars[0])}`;

  let serialized: string;
  if (effectiveOp === '=') {
    serialized = membershipList('=');
  } else if (effectiveOp === '!=') {
    serialized = membershipList('!=');
  } else if (effectiveOp === '^=') {
    serialized = group('OR', (v) => `${field}=${quoteAntlrValue(`${v}*`)}`);
  } else if (effectiveOp === '=^') {
    serialized = group('OR', (v) => `${field}=${quoteAntlrValue(`*${v}`)}`);
  } else if (effectiveOp === '**') {
    serialized = group('OR', (v) => `${field}=${quoteAntlrValue(`*${v}*`)}`);
  } else if (effectiveOp === '!**') {
    // "does not contain" is a negated glob; multi-value means none may match (AND).
    serialized = group('AND', (v) => `${field}!=${quoteAntlrValue(`*${v}*`)}`);
  } else if (effectiveOp === '//' || effectiveOp === '!//') {
    // Regex value already carries its `/` delimiters; force-quote and escape `"`.
    // `!//` ("does not match") is negated, so multi-value combines with AND.
    const eq = effectiveOp === '//' ? '=' : '!=';
    const join = effectiveOp === '//' ? 'OR' : 'AND';
    serialized = group(join, (v) => `${field}${eq}"${v.replace(/"/g, '\\"')}"`);
  } else {
    // Comparison operators (>, >=, <, <=) are single-value: only offered on non-multi numeric fields.
    serialized = `${field}${effectiveOp}${quoteAntlrValue(scalars[0])}`;
  }
  return needsNotWrapper ? `NOT ${serialized}` : serialized;
}

function prepareTokensForAntlr(tokens: ParsedToken[]): ParsedToken[] {
  const hasStructured = tokens.some((token) => token.type === 'FILTER' || token.type === 'EXPRESSION');
  if (!hasStructured) {
    return tokens;
  }
  return tokensForExecutableQuery(tokens);
}

export function parsedQueryToAntlr(tokens: ParsedToken[]): string {
  return prepareTokensForAntlr(tokens)
    .map((token) => {
      switch (token.type) {
        case 'FILTER':
          return filterTokenToAntlr(token);
        case 'LOGIC_BOOL':
          return token.value;
        case 'LOGIC_NOT':
          return token.value;
        case 'EXPRESSION': {
          const prefix = token.expressionType ? (OBS_RELATIONAL_PREFIX[token.expressionType] ?? '') : '';
          return `${prefix}(${parsedQueryToAntlr(token.inner)})`;
        }
        case 'FREE_TEXT':
          return '';
        default: {
          const _exhaustive: never = token;
          return '';
        }
      }
    })
    .filter(Boolean)
    .join(' ');
}
